<script setup lang="ts">
import { formatCurrency, toggleThemeLoading } from '#libs/utils'
import { isEmpty } from 'lodash'
import { computed, ref } from 'vue'
import type { CompleteTheLookProps, CompleteTheLookVariant } from '.'

const { product } = defineProps<{
  product: CompleteTheLookProps['products'][0]
}>()

const selectedVariant = ref<CompleteTheLookVariant | null>(null)
const isAddingToCart = ref(false)
const isAddedToCart = ref(false)
const message = ref('')

const price = computed(() => formatCurrency(selectedVariant.value?.price ?? product.price))
const imageSrc = computed(() => product.featured_image || product.images[0])
const url = computed(
  () => `${window.Shopify.routes.root}products/${product.handle}?variant=${selectedVariant.value?.id}`
)

const variants = computed<CompleteTheLookVariant[]>(() => product.variants)
const variantOptions = computed(() => {
  const sizeOptionIndex = product.options.findIndex((o) => o.toLowerCase() === 'size')
  const sizeOptionKey = `option${sizeOptionIndex + 1}` as 'option1' | 'option2' | 'option3'

  return variants.value.map((variant) => ({
    id: variant.id,
    value: variant.id,
    label: variant[sizeOptionKey],
    available: variant.available
  }))
})

const isSoldOut = computed(() => !product.available || (selectedVariant.value && !selectedVariant.value.available))
const isPreOrder = computed(() => product.tags.includes('Pre-Order') || product.tags.includes('pre-order'))
const isAddToCartDisabled = computed(() => isSoldOut.value || isAddingToCart.value || !isEmpty(message.value))
const addToCartText = computed(() => {
  if (!isEmpty(message.value)) return 'Add to cart failed'
  if (isAddingToCart.value) return 'Adding...'
  if (isAddedToCart.value) return 'Added'
  if (isPreOrder.value) return 'Pre Order'
  if (isSoldOut.value) return 'Sold out'

  return 'Add to cart'
})

const selectVariant = (event: Event) => {
  const target = event.target as HTMLInputElement
  const selectedVariantId = Number(target.value)

  selectedVariant.value = variants.value.find((v) => v.id === selectedVariantId) as CompleteTheLookVariant

  isAddedToCart.value = false
  message.value = ''
}

const addToCart = async () => {
  if (isAddToCartDisabled.value) return

  const id = selectedVariant.value?.id

  if (!id) {
    message.value = 'Please select a variant!'

    return
  }

  try {
    toggleThemeLoading('start')

    if (window.pn) {
      const singleProductSettings = window.pn.settings.singleProductSettings

      const variant = singleProductSettings.find((variant) => variant.n === String(id))

      window.__GLOBAL__.preOrderMessage = <string>variant?.g
    }

    isAddingToCart.value = true
    isAddedToCart.value = false

    const properties = {
      ...(window.__GLOBAL__.preOrderMessage ? { 'PN-Note': window.__GLOBAL__.preOrderMessage } : {})
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id,
        quantity: 1,
        properties
      })
    })

    if (!response.ok) {
      throw new Error('Failed to add to cart')
    }

    isAddedToCart.value = true

    refreshAndOpenCartDrawer()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to add to cart', error)

    message.value = 'Please try again later!'
  } finally {
    toggleThemeLoading('end')
    isAddingToCart.value = false
  }
}

const refreshAndOpenCartDrawer = () => {
  document.dispatchEvent(new CustomEvent('cart:refresh'))

  const cartDrawerEl = document.querySelector('cart-drawer')

  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(cartDrawerEl as any)?.show()
    isAddedToCart.value = false
  }, 750)
}
</script>

<template>
  <div class="complete-product-card">
    <div class="tw-flex tw-gap-3">
      <div class="tw-aspect-[115/170] tw-w-1/4 tw-min-w-[25%] tw-self-start">
        <a :href="url" class="tw-block tw-h-full tw-w-full">
          <img
            v-if="imageSrc"
            :src="imageSrc"
            :alt="product.title"
            loading="lazy"
            class="tw-h-full tw-w-full tw-object-cover"
          />
        </a>
      </div>
      <div class="tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-overflow-hidden">
        <div class="complete-product-card__header">
          <a :href="url" class="tw-block tw-w-full">
            <h3 class="h6" v-text="product.title" />
          </a>

          <p class="h6 text-subdued tw-mt-1" v-text="price" />
        </div>

        <div class="complete-product-card__footer">
          <div
            class="tw-flex tw-w-full tw-flex-col tw-justify-between tw-gap-4 smd-to-2lg:tw-flex-row smd-to-2lg:tw-items-center"
          >
            <div
              class="complete-product-card__options-list tw-flex tw-justify-between tw-gap-1 tw-overflow-auto smd-to-2lg:tw-flex-1"
            >
              <div v-for="option in variantOptions" :key="option.id" class="tw-relative tw-flex 2lg:tw-min-w-8">
                <input
                  :id="option.id.toString()"
                  name="complete-product-card-selected-variant-id"
                  class="tw-sr-only"
                  type="radio"
                  :disabled="isAddingToCart"
                  :value="option.value"
                  @change.prevent="selectVariant"
                />
                <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
                <label
                  class="tw-relative tw-cursor-pointer tw-border-b tw-border-solid tw-p-2"
                  :class="[
                    option.value === selectedVariant?.id ? 'tw-border-black' : 'tw-border-transparent',
                    { 'tw-line-through tw-opacity-30': !option.available }
                  ]"
                  :for="option.id.toString()"
                  v-text="option.label"
                />
              </div>
            </div>

            <button
              class="button tw-w-full tw-py-1 disabled:tw-opacity-50 smd-to-2lg:tw-max-w-[35%]"
              type="button"
              :disabled="isAddToCartDisabled || !selectedVariant"
              @click.prevent="addToCart"
            >
              {{ addToCartText }}
            </button>
          </div>

          <p v-if="message" class="tw-mt-1 tw-text-red" v-text="message" />
        </div>
      </div>
    </div>
  </div>
</template>
